'use client';

import { useState, useEffect } from 'react';
import SpeciesCard from './SpeciesCard';
import CategoryFilter from './CategoryFilter';
import SearchBar from './SearchBar';
import ConservationBanner from './ConservationBanner';
import SpeciesModal from './SpeciesModal';
import Icon from '@/components/ui/AppIcon';


interface DiveSite {
  name: string;
  probability: string;
}

interface SeasonalAvailability {
  season: string;
  probability: string;
}

interface Species {
  id: number;
  commonName: string;
  scientificName: string;
  category: string;
  image: string;
  alt: string;
  conservationStatus: string;
  sightingProbability: string;
  description: string;
  physicalDescription: string;
  behavior: string;
  size: string;
  habitat: string;
  bestDiveSites: DiveSite[];
  seasonalAvailability: SeasonalAvailability[];
  interestingFacts: string[];
  conservationInfo: string;
}

interface Category {
  id: string;
  name: string;
  icon: string;
  count: number;
}

const MarineLifeInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories: Category[] = [
  { id: 'all', name: 'All Species', icon: 'SparklesIcon', count: 29 },
  { id: 'fish', name: 'Fish', icon: 'FireIcon', count: 15 },
  { id: 'invertebrates', name: 'Invertebrates', icon: 'StarIcon', count: 8 },
  { id: 'sea-turtles', name: 'Sea Turtles', icon: 'ShieldCheckIcon', count: 3 },
  { id: 'marine-mammals', name: 'Marine Mammals', icon: 'HeartIcon', count: 2 },
  { id: 'rays', name: 'Rays & Sharks', icon: 'BoltIcon', count: 1 }];


  const speciesData: Species[] = [
  {
    id: 1,
    commonName: 'Green Sea Turtle',
    scientificName: 'Chelonia mydas',
    category: 'Sea Turtles',
    image: "https://images.unsplash.com/photo-1471079502516-250c19af6928",
    alt: 'Green sea turtle swimming gracefully through clear blue Mediterranean waters with sunlight filtering from above',
    conservationStatus: 'Endangered',
    sightingProbability: 'High',
    description: 'The Green Sea Turtle is one of the most iconic marine species in Cyprus waters, known for its graceful swimming and gentle nature.',
    physicalDescription: 'Large sea turtle with a smooth, heart-shaped carapace ranging from olive to brown. Adults can weigh up to 200kg with a shell length of 1-1.2 meters. Named for the greenish color of their fat, not their shell.',
    behavior: 'Herbivorous as adults, feeding primarily on seagrass and algae. Often seen resting on the seabed or swimming near the surface. Known to return to Cyprus beaches for nesting during summer months.',
    size: '1-1.2 meters shell length, up to 200kg',
    habitat: 'Shallow coastal waters, seagrass beds, rocky reefs',
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'Very High' },
    { name: 'Green Bay', probability: 'High' },
    { name: 'Turtle Bay', probability: 'Very High' },
    { name: 'Amphora Reef', probability: 'Moderate' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'High' },
    { season: 'Summer', probability: 'Very High' },
    { season: 'Autumn', probability: 'High' },
    { season: 'Winter', probability: 'Moderate' }],

    interestingFacts: [
    'Cyprus is one of the most important nesting sites for Green Sea Turtles in the Mediterranean',
    'They can hold their breath for up to 5 hours while resting',
    'Female turtles return to the same beach where they were born to lay their eggs',
    'They can live for over 80 years in the wild'],

    conservationInfo: 'Listed as Endangered on the IUCN Red List. Protected under Cyprus law and EU Habitats Directive. Nesting beaches are monitored and protected. Divers should maintain a distance of at least 3 meters and never touch or chase turtles.'
  },
  {
    id: 2,
    commonName: 'Dusky Grouper',
    scientificName: 'Epinephelus marginatus',
    category: 'Fish',
    image: '/assets/images/dusky-grouper-cyprus-epinephelus-marginatus-1777436562350.jpg',
    alt: 'Dusky Grouper (Epinephelus marginatus) close-up on a Cyprus reef',
    caption: 'A Dusky Grouper resting on a rocky reef in Cyprus, a classic Mediterranean dive encounter',
    conservationStatus: 'Vulnerable',
    sightingProbability: 'Moderate',
    description: 'The Dusky Grouper is one of the most iconic and powerful reef predators in the Mediterranean. Known for its large size, expressive features, and slow, confident movement, it is often encountered resting near rocky reefs, caves, and wrecks. Though typically solitary, it can be surprisingly curious around divers and is considered a highlight of many Cyprus dives.',
    physicalDescription: 'Robust body with mottled brown to grey coloration. Can reach up to 1.5 meters in length and weigh over 60kg. Large mouth and prominent lower jaw. Older individuals develop a distinctive hump on their head.',
    behavior: 'Solitary and territorial, often found in caves and rocky overhangs. Curious about divers and may approach closely. Feeds on fish, octopus, and crustaceans. Can change color rapidly when excited or threatened.',
    size: 'Up to 1.5 meters, 60kg+',
    habitat: 'Rocky reefs, caves, wrecks, 10-50 meters depth',
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'High' },
    { name: 'Chapel Reef', probability: 'Moderate' },
    { name: 'Cyclops Cave', probability: 'High' },
    { name: 'Petra tou Romiou', probability: 'Moderate' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Moderate' },
    { season: 'Summer', probability: 'High' },
    { season: 'Autumn', probability: 'High' },
    { season: 'Winter', probability: 'Moderate' }],

    interestingFacts: [
    'They are protogynous hermaphrodites - all are born female and some change to male later in life',
    'Can live for over 50 years',
    'Their populations have declined by over 90% in some Mediterranean areas due to overfishing',
    'They recognize individual divers and can become quite friendly with regular visitors'],

    conservationInfo: 'Listed as Vulnerable on the IUCN Red List. Protected in Cyprus waters with fishing restrictions. Important indicator species for marine reserve health. Divers should not feed or touch these fish.'
  },
  {
    id: 3,
    commonName: 'Octopus',
    scientificName: 'Octopus vulgaris',
    category: 'Invertebrates',
    image: "https://images.unsplash.com/photo-1514093167082-5f19daac7ff3",
    alt: 'Common octopus with reddish-brown skin texture camouflaged against rocky Mediterranean seabed with extended tentacles',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Very High',
    description: 'The Common Octopus is one of the most intelligent invertebrates, masters of camouflage and fascinating to observe in Cyprus waters.',
    physicalDescription: 'Eight arms with two rows of suckers on each. Can change color and texture instantly to match surroundings. Body can reach 25cm with arms extending up to 1 meter. Typically reddish-brown but can display various colors.',
    behavior: 'Highly intelligent and curious. Active hunters, primarily nocturnal but often seen during day dives. Uses jet propulsion to escape threats. Creates dens in rocky crevices marked by shells and debris. Can squeeze through tiny openings.',
    size: 'Body 25cm, arms up to 1 meter total span',
    habitat: 'Rocky reefs, caves, sandy areas, 0-200 meters depth',
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'Very High' },
    { name: 'Green Bay', probability: 'Very High' },
    { name: 'Chapel Reef', probability: 'High' },
    { name: 'Amphora Reef', probability: 'Very High' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Very High' },
    { season: 'Summer', probability: 'Very High' },
    { season: 'Autumn', probability: 'Very High' },
    { season: 'Winter', probability: 'High' }],

    interestingFacts: [
    'They have three hearts and blue blood',
    'Can change color in less than a second using specialized skin cells called chromatophores',
    'Each arm has a mind of its own with two-thirds of neurons in the arms rather than the brain',
    'They are masters of escape, able to squeeze through any opening larger than their beak'],

    conservationInfo: 'Currently listed as Least Concern but populations are monitored. Important part of the marine ecosystem. Fishing is regulated with seasonal closures. Divers should observe without touching as they can be stressed by interaction.'
  },
  {
    id: 4,
    commonName: 'Mediterranean Moray Eel',
    scientificName: 'Muraena helena',
    category: 'Fish',
    image: "https://images.unsplash.com/photo-1559897202-7fc939ce9db2",
    alt: 'Mediterranean moray eel with distinctive mottled brown and yellow pattern emerging from rocky crevice with mouth slightly open',
    conservationStatus: 'Least Concern',
    sightingProbability: 'High',
    description: 'The Mediterranean Moray Eel is a common sight in Cyprus waters, often misunderstood but fascinating to observe in their rocky homes.',
    physicalDescription: 'Elongated snake-like body with mottled brown and yellow coloration. Can reach up to 1.5 meters in length. No pectoral or pelvic fins. Constantly opens and closes mouth to breathe, which can appear threatening but is normal behavior.',
    behavior: 'Nocturnal predator but often seen during day with head protruding from rocky crevices. Feeds on fish, octopus, and crustaceans. Not aggressive unless provoked. Poor eyesight but excellent sense of smell. Territorial of their chosen den.',
    size: 'Up to 1.5 meters length',
    habitat: 'Rocky reefs, caves, crevices, 0-50 meters depth',
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'Very High' },
    { name: 'Chapel Reef', probability: 'High' },
    { name: 'Cyclops Cave', probability: 'Very High' },
    { name: 'Amphora Reef', probability: 'High' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'High' },
    { season: 'Summer', probability: 'Very High' },
    { season: 'Autumn', probability: 'High' },
    { season: 'Winter', probability: 'Moderate' }],

    interestingFacts: [
    'They have a second set of jaws in their throat called pharyngeal jaws',
    'Their skin secretes a protective mucus that can be toxic to other fish',
    'They can tie themselves in knots to escape predators or remove slime',
    'Despite their fearsome appearance, they rarely bite divers unless provoked'],

    conservationInfo: 'Listed as Least Concern. Common throughout Cyprus waters. Important predator in the reef ecosystem. Divers should never put hands in crevices and maintain respectful distance. Never feed moray eels as this can lead to aggressive behavior.'
  },
  {
    id: 5,
    commonName: 'Barracuda',
    scientificName: 'Sphyraena sphyraena',
    category: 'Fish',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1184fbd81-1772284537113.png",
    alt: 'Sleek silver barracuda with elongated body and prominent jaw swimming in open blue Mediterranean water',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Moderate',
    description: 'The European Barracuda is a sleek predator often seen patrolling Cyprus reefs, impressive in their hunting prowess and streamlined beauty.',
    physicalDescription: 'Elongated, streamlined body with silver coloration and darker back. Prominent lower jaw with sharp teeth visible. Can reach up to 1.6 meters in length. Large eyes and forked tail for rapid acceleration.',
    behavior: 'Often solitary or in small groups. Curious about divers and may follow at a distance. Ambush predator that uses speed to catch prey. Sometimes forms large schools in open water. More active during dawn and dusk.',
    size: 'Up to 1.6 meters length',
    habitat: 'Open water, reef edges, seagrass beds, 0-100 meters',
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'High' },
    { name: 'Green Bay', probability: 'Moderate' },
    { name: 'Petra tou Romiou', probability: 'Moderate' },
    { name: 'Cyclops Cave', probability: 'Moderate' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Moderate' },
    { season: 'Summer', probability: 'High' },
    { season: 'Autumn', probability: 'High' },
    { season: 'Winter', probability: 'Low' }],

    interestingFacts: [
    'They can swim at speeds up to 36 mph in short bursts',
    'Their teeth are so sharp they can slice through fishing line',
    'They are attracted to shiny objects, which they may mistake for prey',
    'Despite their fearsome appearance, attacks on humans are extremely rare'],

    conservationInfo: 'Listed as Least Concern. Populations are stable in Cyprus waters. Important apex predator in the marine ecosystem. Divers should avoid wearing shiny jewelry and maintain calm movements around barracuda.'
  },
  {
    id: 6,
    commonName: 'Loggerhead Sea Turtle',
    scientificName: 'Caretta caretta',
    category: 'Sea Turtles',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12c34e06c-1772357911167.png",
    alt: 'Loggerhead sea turtle with reddish-brown shell swimming near sandy seabed in clear Mediterranean waters',
    conservationStatus: 'Vulnerable',
    sightingProbability: 'High',
    description: 'The Loggerhead Sea Turtle is another precious visitor to Cyprus waters, slightly smaller than Green Turtles but equally captivating.',
    physicalDescription: 'Reddish-brown carapace with a large head (hence the name). Adults typically 70-95cm shell length and weigh 80-200kg. Five pairs of coastal scutes on the shell. Powerful jaws adapted for crushing prey.',
    behavior: 'Omnivorous, feeding on jellyfish, crustaceans, and mollusks. Often seen resting on the seabed or swimming in open water. Nests on Cyprus beaches from May to August. Can dive to depths over 200 meters.',
    size: '70-95cm shell length, 80-200kg',
    habitat: 'Coastal waters, open ocean, sandy bays, rocky reefs',
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'High' },
    { name: 'Green Bay', probability: 'Very High' },
    { name: 'Turtle Bay', probability: 'Very High' },
    { name: 'Petra tou Romiou', probability: 'Moderate' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'High' },
    { season: 'Summer', probability: 'Very High' },
    { season: 'Autumn', probability: 'High' },
    { season: 'Winter', probability: 'Moderate' }],

    interestingFacts: [
    'Cyprus hosts the second largest nesting population of Loggerheads in the Mediterranean',
    'They can migrate thousands of kilometers between feeding and nesting grounds',
    'Temperature of the sand determines the sex of hatchlings',
    'They can live for 50-70 years in the wild'],

    conservationInfo: 'Listed as Vulnerable on the IUCN Red List. Protected under Cyprus law and international conventions. Nesting beaches are strictly monitored. Divers must maintain minimum 3-meter distance and never use flash photography near turtles.'
  },
  {
    id: 7,
    commonName: 'Painted Comber',
    scientificName: 'Serranus scriba',
    category: 'Fish',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ddc91f50-1777036168429.png",
    alt: 'Colorful painted comber fish with distinctive orange and blue striped pattern swimming near rocky Mediterranean reef',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Very High',
    description: 'The Painted Comber is one of the most colorful and commonly seen fish in Cyprus waters, easily recognizable by its distinctive markings.',
    physicalDescription: 'Small fish with orange-red body decorated with bright blue vertical stripes and spots. Distinctive blue markings on the head resembling Arabic script (hence "scriba"). Reaches up to 25cm in length.',
    behavior: 'Territorial and curious, often approaching divers closely. Hermaphroditic species that can change sex. Feeds on small fish and crustaceans. Active during the day, resting in crevices at night. Often seen in pairs or small groups.',
    size: 'Up to 25cm length',
    habitat: 'Rocky reefs, seagrass beds, 5-150 meters depth',
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'Very High' },
    { name: 'Chapel Reef', probability: 'Very High' },
    { name: 'Green Bay', probability: 'Very High' },
    { name: 'Amphora Reef', probability: 'Very High' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Very High' },
    { season: 'Summer', probability: 'Very High' },
    { season: 'Autumn', probability: 'Very High' },
    { season: 'Winter', probability: 'High' }],

    interestingFacts: [
    'The blue markings on their head resemble Arabic writing, giving them their scientific name',
    'They are simultaneous hermaphrodites, possessing both male and female reproductive organs',
    'Very photogenic and popular with underwater photographers',
    'They can live up to 12 years'],

    conservationInfo: 'Listed as Least Concern. Very common throughout Cyprus waters. Important part of the reef ecosystem. No special protection measures needed but should not be harassed for photographs.'
  },
  {
    id: 8,
    commonName: 'Mediterranean Jellyfish',
    scientificName: 'Cotylorhiza tuberculata',
    category: 'Invertebrates',
    image: "https://images.unsplash.com/photo-1639913133517-a39e15cbc99f",
    alt: 'Fried egg jellyfish with distinctive yellow center and white translucent bell floating in blue Mediterranean water',
    conservationStatus: 'Least Concern',
    sightingProbability: 'High',
    description: 'The Fried Egg Jellyfish is a beautiful and relatively harmless jellyfish commonly seen in Cyprus waters during summer months.',
    physicalDescription: 'Distinctive appearance with a yellow-orange center resembling a fried egg, surrounded by a white translucent bell. Can reach up to 35cm in diameter. Eight oral arms with numerous small appendages.',
    behavior: 'Passive drifter that moves with currents. Feeds on small plankton and fish larvae. Often found in large aggregations during summer. Provides shelter for small fish that swim among its tentacles.',
    size: 'Up to 35cm bell diameter',
    habitat: 'Surface waters, coastal areas, harbors, 0-10 meters',
    bestDiveSites: [
    { name: 'Green Bay', probability: 'Very High' },
    { name: 'Turtle Bay', probability: 'High' },
    { name: 'Zenobia Wreck', probability: 'Moderate' },
    { name: 'Petra tou Romiou', probability: 'High' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Moderate' },
    { season: 'Summer', probability: 'Very High' },
    { season: 'Autumn', probability: 'High' },
    { season: 'Winter', probability: 'Low' }],

    interestingFacts: [
    'Their sting is very mild and rarely causes problems for humans',
    'They have a symbiotic relationship with small fish that live among their tentacles',
    'They are 95% water',
    'Large blooms can indicate changes in ocean conditions'],

    conservationInfo: 'Listed as Least Concern. Common in Mediterranean waters. Important food source for sea turtles. Divers should avoid touching but they pose minimal threat. Large blooms are natural phenomena.'
  },
  {
    id: 9,
    commonName: 'Damselfish',
    scientificName: 'Chromis chromis',
    category: 'Fish',
    image: "https://images.unsplash.com/photo-1701691796530-cd5bdc1e7bd0",
    alt: 'School of small dark damselfish swimming together above rocky Mediterranean reef formation',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Very High',
    description: 'The Damselfish is one of the most abundant fish in Cyprus waters, forming large schools that create living clouds above the reefs.',
    physicalDescription: 'Small fish with dark brown to black coloration. Juveniles are bright blue. Deeply forked tail. Reaches up to 15cm in length. Large eyes relative to body size.',
    behavior: 'Forms large aggregations above rocky reefs. Feeds on plankton in the water column. Territorial during breeding season when males guard nests. Quick and agile swimmers that dart into crevices when threatened.',
    size: 'Up to 15cm length',
    habitat: 'Rocky reefs, caves, wrecks, 3-40 meters depth',
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'Very High' },
    { name: 'Chapel Reef', probability: 'Very High' },
    { name: 'Cyclops Cave', probability: 'Very High' },
    { name: 'Amphora Reef', probability: 'Very High' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Very High' },
    { season: 'Summer', probability: 'Very High' },
    { season: 'Autumn', probability: 'Very High' },
    { season: 'Winter', probability: 'High' }],

    interestingFacts: [
    'Juveniles are electric blue and often mistaken for tropical fish',
    'Males become very aggressive when guarding eggs',
    'They can live up to 12 years',
    'Their schools can contain thousands of individuals'],

    conservationInfo: 'Listed as Least Concern. Extremely abundant throughout Cyprus waters. Important prey species for larger fish. No conservation concerns. Excellent indicator of healthy reef ecosystems.'
  },
  {
    id: 10,
    commonName: 'Scorpionfish',
    scientificName: 'Scorpaena porcus',
    category: 'Fish',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d5290311-1770411351873.png",
    alt: 'Well-camouflaged scorpionfish with mottled brown and red coloring resting motionless on rocky Mediterranean seabed',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Moderate',
    description: 'The Scorpionfish is a master of camouflage and one of the most challenging species to spot, but rewarding when found.',
    physicalDescription: 'Heavily camouflaged with mottled brown, red, and yellow coloration. Numerous skin flaps and spines. Large head with upturned mouth. Reaches up to 25cm in length. Venomous spines on dorsal fin.',
    behavior: 'Ambush predator that remains motionless for hours. Relies on camouflage to catch prey and avoid predators. Feeds on small fish and crustaceans. Nocturnal but often seen during day dives. Buries itself partially in sand.',
    size: 'Up to 25cm length',
    habitat: 'Rocky reefs, sandy bottoms, seagrass beds, 5-200 meters',
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'Moderate' },
    { name: 'Chapel Reef', probability: 'High' },
    { name: 'Green Bay', probability: 'Moderate' },
    { name: 'Amphora Reef', probability: 'High' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Moderate' },
    { season: 'Summer', probability: 'High' },
    { season: 'Autumn', probability: 'High' },
    { season: 'Winter', probability: 'Moderate' }],

    interestingFacts: [
    'Their venom can cause extremely painful stings that last for hours',
    'They can change color to match their surroundings',
    'They can survive out of water for up to 24 hours',
    'They are considered a delicacy in Mediterranean cuisine'],

    conservationInfo: 'Listed as Least Concern. Common but easily overlooked due to camouflage. Important predator in the ecosystem. Divers must be extremely careful not to touch or step on them as their venomous spines can cause serious injury.'
  },
  {
    id: 11,
    commonName: 'Flying Gurnard',
    scientificName: 'Dactylopterus volitans',
    category: 'Fish',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_101f99647-1770411352472.png",
    alt: 'Flying gurnard fish with spectacular fan-like pectoral fins spread wide showing blue and red patterns on sandy seabed',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Moderate',
    description: 'The Flying Gurnard is one of the most spectacular fish in Cyprus waters, famous for its enormous colorful pectoral fins.',
    physicalDescription: 'Distinctive fish with huge wing-like pectoral fins decorated with blue spots and red margins. Brown body with bony plates. Can reach up to 50cm in length. Large head with prominent eyes.',
    behavior: 'Walks on the seabed using modified pelvic fins. Spreads large pectoral fins when startled or displaying. Feeds on small crustaceans and mollusks by stirring up sand. Solitary and territorial.',
    size: 'Up to 50cm length',
    habitat: 'Sandy and muddy bottoms, 10-100 meters depth',
    bestDiveSites: [
    { name: 'Green Bay', probability: 'High' },
    { name: 'Zenobia Wreck', probability: 'Moderate' },
    { name: 'Amphora Reef', probability: 'Moderate' },
    { name: 'Chapel Reef', probability: 'Low' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Moderate' },
    { season: 'Summer', probability: 'High' },
    { season: 'Autumn', probability: 'High' },
    { season: 'Winter', probability: 'Low' }],

    interestingFacts: [
    'Despite the name, they cannot actually fly but glide short distances',
    'Their pectoral fins can span up to 30cm when fully extended',
    'They make grunting sounds by vibrating their swim bladder',
    'Highly prized by underwater photographers for their spectacular displays'],

    conservationInfo: 'Listed as Least Concern. Uncommon but regularly seen in Cyprus waters. No specific conservation measures needed. Divers should approach slowly to witness their spectacular fin displays.'
  },
  {
    id: 12,
    commonName: 'Seahorse',
    scientificName: 'Hippocampus hippocampus',
    category: 'Fish',
    image: "https://images.unsplash.com/photo-1666735898274-3e20ddbef4ec",
    alt: 'Small seahorse with curved body and prehensile tail clinging to seagrass in shallow Mediterranean waters',
    conservationStatus: 'Near Threatened',
    sightingProbability: 'Low',
    description: 'The Short-snouted Seahorse is a rare and magical find in Cyprus waters, requiring patience and keen observation to spot.',
    physicalDescription: 'Small fish with horse-like head and prehensile tail. Variable coloration from brown to yellow to green. Reaches up to 15cm in length. Bony plates instead of scales. Males have brood pouch.',
    behavior: 'Slow-moving, anchors to seagrass or algae with prehensile tail. Feeds on tiny crustaceans by sucking them through tubular snout. Males carry and give birth to young. Monogamous pairs perform daily greeting dances.',
    size: 'Up to 15cm length',
    habitat: 'Seagrass beds, algae-covered rocks, 1-20 meters depth',
    bestDiveSites: [
    { name: 'Green Bay', probability: 'Moderate' },
    { name: 'Turtle Bay', probability: 'Low' },
    { name: 'Amphora Reef', probability: 'Low' },
    { name: 'Chapel Reef', probability: 'Low' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Low' },
    { season: 'Summer', probability: 'Moderate' },
    { season: 'Autumn', probability: 'Low' },
    { season: 'Winter', probability: 'Low' }],

    interestingFacts: [
    'Males become pregnant and give birth to live young',
    'They can move each eye independently',
    'They have no stomach and must eat constantly',
    'They are the slowest-moving fish in the ocean'],

    conservationInfo: 'Listed as Near Threatened due to habitat loss and collection for traditional medicine. Protected under Cyprus law. Extremely rare sightings should be reported to marine conservation authorities. Never touch or disturb seahorses.'
  },
  {
    id: 13,
    commonName: 'Sea Urchin',
    scientificName: 'Paracentrotus lividus',
    category: 'Invertebrates',
    image: "https://images.unsplash.com/photo-1627496972106-3b71670ccfa0",
    alt: 'Purple sea urchin with long spines covering rocky Mediterranean seabed surface',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Very High',
    description: 'The Purple Sea Urchin is one of the most common invertebrates in Cyprus waters, found on virtually every rocky reef and playing a crucial role in the ecosystem.',
    physicalDescription: 'Spherical body covered with purple to brown spines. Can reach up to 7cm in diameter with spines extending another 3-4cm. Five-part radial symmetry. Mouth (Aristotle\'s lantern) on underside with five teeth.',
    behavior: 'Grazes on algae and seagrass using specialized teeth. Nocturnal but often visible during day. Uses tube feet and spines for movement. Creates depressions in soft rock where they shelter. Important herbivore controlling algae growth.',
    size: 'Up to 7cm body diameter, spines 3-4cm',
    habitat: 'Rocky reefs, seagrass beds, caves, 0-80 meters depth',
    bestDiveSites: [
    { name: 'Chapel Reef', probability: 'Very High' },
    { name: 'Zenobia Wreck', probability: 'Very High' },
    { name: 'Cyclops Cave', probability: 'Very High' },
    { name: 'Amphora Reef', probability: 'Very High' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Very High' },
    { season: 'Summer', probability: 'Very High' },
    { season: 'Autumn', probability: 'Very High' },
    { season: 'Winter', probability: 'Very High' }],

    interestingFacts: [
    'Their teeth are self-sharpening and continuously grow throughout their life',
    'They can live for over 50 years in the wild',
    'Overfishing of their predators has led to urchin barrens in some Mediterranean areas',
    'They are considered a delicacy in Mediterranean cuisine and are harvested commercially'],

    conservationInfo: 'Listed as Least Concern but populations are monitored due to commercial harvesting. Important grazer in reef ecosystems. Fishing is regulated with size limits and seasonal closures. Divers should avoid touching as spines can cause painful puncture wounds.'
  },
  {
    id: 14,
    commonName: 'Lionfish',
    scientificName: 'Pterois miles',
    category: 'Fish',
    image: "https://images.unsplash.com/photo-1642703746482-a8393214657d",
    alt: 'Striking lionfish with red and white striped pattern and elaborate venomous fin rays spread wide in Mediterranean waters',
    conservationStatus: 'Invasive Species',
    sightingProbability: 'Moderate',
    description: 'The Devil Firefish (Lionfish) is an invasive species in Cyprus waters, originally from the Red Sea. While beautiful, it poses a significant threat to native Mediterranean fish populations.',
    physicalDescription: 'Spectacular fish with red, white, and brown zebra-striped pattern. Long, separated venomous spines on dorsal, pelvic, and anal fins. Large pectoral fins like fans. Can reach up to 35cm in length.',
    behavior: 'Ambush predator that hunts at dawn and dusk. Uses large pectoral fins to corner prey against reefs. Voracious appetite consuming large numbers of small fish and crustaceans. Solitary and territorial. No natural predators in Mediterranean.',
    size: 'Up to 35cm length',
    habitat: 'Rocky reefs, caves, wrecks, 10-50 meters depth',
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'High' },
    { name: 'Chapel Reef', probability: 'Moderate' },
    { name: 'Cyclops Cave', probability: 'Moderate' },
    { name: 'Amphora Reef', probability: 'Moderate' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Moderate' },
    { season: 'Summer', probability: 'High' },
    { season: 'Autumn', probability: 'High' },
    { season: 'Winter', probability: 'Low' }],

    interestingFacts: [
    'Arrived in Mediterranean through the Suez Canal (Lessepsian migration)',
    'Their venom can cause extremely painful stings requiring medical attention',
    'A single lionfish can reduce native fish populations by up to 90% in an area',
    'They have no natural predators in the Mediterranean, allowing rapid population growth'],

    conservationInfo: 'Listed as Invasive Species - a major threat to Mediterranean biodiversity. Removal programs are encouraged in Cyprus. Divers trained in safe handling are encouraged to report sightings. Never touch - venomous spines can cause severe injury. Some dive centers organize lionfish removal dives.'
  },
  {
    id: 15,
    commonName: 'White Sea Bream',
    scientificName: 'Diplodus sargus',
    category: 'Fish',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1efd95735-1772284536263.png",
    alt: 'Silver white sea bream with distinctive black vertical bands swimming near rocky Mediterranean reef',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Very High',
    description: 'The White Sea Bream is one of the most common and recognizable fish in Cyprus waters, often seen in large schools around rocky reefs and seagrass beds.',
    physicalDescription: 'Oval-shaped body with silver-white coloration and 8-9 distinctive vertical black bands. Black spot on tail base. Can reach up to 45cm in length and weigh 2kg. Strong teeth adapted for crushing shellfish.',
    behavior: 'Forms schools near rocky reefs and seagrass beds. Feeds on mollusks, crustaceans, sea urchins, and algae. Curious and often approaches divers. Active during the day. Juveniles often found in very shallow water.',
    size: 'Up to 45cm length, 2kg weight',
    habitat: 'Rocky reefs, seagrass beds, sandy areas, 0-50 meters depth',
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'Very High' },
    { name: 'Chapel Reef', probability: 'Very High' },
    { name: 'Green Bay', probability: 'Very High' },
    { name: 'Amphora Reef', probability: 'Very High' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Very High' },
    { season: 'Summer', probability: 'Very High' },
    { season: 'Autumn', probability: 'Very High' },
    { season: 'Winter', probability: 'High' }],

    interestingFacts: [
    'They are protandrous hermaphrodites - born male and can change to female later',
    'Their strong teeth can crush sea urchin shells and mollusk shells',
    'Highly prized by recreational fishermen for their fighting ability and taste',
    'They can live for over 10 years in the wild'],

    conservationInfo: 'Listed as Least Concern. Very common throughout Cyprus waters. Important commercial and recreational fishing species with size limits and seasonal restrictions. Populations are stable but monitored. Divers often see them in large numbers.'
  },
  {
    id: 16,
    commonName: 'European Sea Bass',
    scientificName: 'Dicentrarchus labrax',
    category: 'Fish',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1184fbd81-1772284537113.png",
    alt: 'Sleek silver European sea bass with streamlined body swimming in clear Mediterranean coastal waters',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Moderate',
    description: 'The European Sea Bass is a prized game fish and important commercial species in Cyprus waters, known for its sleek appearance and powerful swimming.',
    physicalDescription: 'Streamlined silver body with darker grey-blue back. Two dorsal fins, the first with sharp spines. Can reach up to 1 meter in length and weigh 12kg. Large mouth with small teeth. Distinctive gill cover with sharp edge.',
    behavior: 'Predatory fish feeding on smaller fish, crustaceans, and mollusks. Often found in small groups near rocky reefs and around structures. Juveniles prefer shallow coastal waters and estuaries. Adults venture into deeper water. Active hunters, especially at dawn and dusk.',
    size: 'Up to 1 meter length, 12kg weight',
    habitat: 'Rocky reefs, sandy areas, coastal waters, 10-100 meters depth',
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'High' },
    { name: 'Green Bay', probability: 'Moderate' },
    { name: 'Petra tou Romiou', probability: 'Moderate' },
    { name: 'Chapel Reef', probability: 'Moderate' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'High' },
    { season: 'Summer', probability: 'High' },
    { season: 'Autumn', probability: 'Moderate' },
    { season: 'Winter', probability: 'Low' }],

    interestingFacts: [
    'They can live for over 25 years and continue growing throughout their life',
    'Highly intelligent fish that learn to avoid fishing gear',
    'One of the most valuable commercial fish species in the Mediterranean',
    'They have excellent vision and can hunt in low light conditions'],

    conservationInfo: 'Listed as Least Concern but heavily fished commercially and recreationally. Strict size limits (minimum 42cm) and seasonal closures protect spawning populations in Cyprus. Important indicator of healthy coastal ecosystems. Populations are carefully monitored.'
  },
  {
    id: 17,
    commonName: 'Spanish Dancer',
    scientificName: 'Hexabranchus sanguineus',
    category: 'Invertebrates',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10ed9b481-1772284534216.png",
    alt: 'Vibrant red Spanish dancer nudibranch with ruffled edges undulating gracefully through Mediterranean waters at night',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Low',
    description: 'The Spanish Dancer is the largest nudibranch in the world and one of the most spectacular sights in Cyprus waters. This rare nocturnal creature puts on an unforgettable display when swimming.',
    physicalDescription: 'Large sea slug with brilliant red to orange coloration and ruffled edges. Can reach up to 60cm in length when fully extended. Flat, oval body with undulating margins. Six gills visible on back. Releases red toxins when threatened.',
    behavior: 'Primarily nocturnal, hiding in crevices during the day. When disturbed, swims by rhythmically undulating its body in a dance-like motion (hence the name). Feeds on sponges. Lays distinctive red ribbon-like egg masses. Solitary except during mating.',
    size: 'Up to 60cm length when extended',
    habitat: 'Rocky reefs, caves, coral areas, 5-90 meters depth',
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'Low' },
    { name: 'Cyclops Cave', probability: 'Moderate' },
    { name: 'Chapel Reef', probability: 'Low' },
    { name: 'Amphora Reef', probability: 'Low' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Low' },
    { season: 'Summer', probability: 'Moderate' },
    { season: 'Autumn', probability: 'Low' },
    { season: 'Winter', probability: 'Low' }],

    interestingFacts: [
    'The largest nudibranch species in the world',
    'Their spectacular swimming display resembles a flamenco dancer\'s dress',
    'They are toxic to predators due to compounds derived from their sponge diet',
    'Night dives offer the best chance of seeing them swimming',
    'Their egg ribbons can contain over one million eggs'],

    conservationInfo: 'Listed as Least Concern but rarely encountered due to nocturnal habits. Sightings are special events for divers. Never touch as they can release toxic mucus. Important indicator of healthy reef ecosystems. Report sightings to contribute to marine biodiversity records.'
  }];



  const filteredSpecies = speciesData.filter((species) => {
    const matchesCategory = activeCategory === 'all' || species.category.toLowerCase().replace(/\s+/g, '-') === activeCategory;
    const matchesSearch = species.commonName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    species.scientificName.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleViewDetails = (id: number) => {
    if (!isHydrated) return;
    const species = speciesData.find((s) => s.id === id);
    setSelectedSpecies(species || null);
  };

  const handleCloseModal = () => {
    if (!isHydrated) return;
    setSelectedSpecies(null);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded-lg w-3/4"></div>
            <div className="h-64 bg-muted rounded-xl"></div>
            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) =>
              <div key={i} className="h-96 bg-muted rounded-xl"></div>
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="space-y-8">
      <ConservationBanner />

      <div className="grid lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <CategoryFilter
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory} />

        </div>

        <div className="lg:col-span-3 space-y-6">
          <SearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />

          <div className="flex items-center justify-between">
            <p className="text-muted-foreground">
              Showing <span className="font-bold text-foreground">{filteredSpecies.length}</span> species
            </p>
          </div>

          {filteredSpecies.length === 0 ?
          <div className="text-center py-16">
              <div className="w-24 h-24 mx-auto mb-4 bg-muted/30 rounded-full flex items-center justify-center">
                <Icon name="MagnifyingGlassIcon" size={48} className="text-muted-foreground" />
              </div>
              <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                No species found
              </h3>
              <p className="text-muted-foreground">
                Try adjusting your search or filter criteria
              </p>
            </div> :

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSpecies.map((species) =>
            <SpeciesCard
              key={species.id}
              species={species}
              onViewDetails={handleViewDetails} />

            )}
            </div>
          }
        </div>
      </div>

      <SpeciesModal species={selectedSpecies} onClose={handleCloseModal} />
    </div>);

};

export default MarineLifeInteractive;